import type { Locale } from "@/i18n-config";

/**
 * Where a gallery's contents lead — the ONE place that rule lives.
 *
 * WHY IT IS A MODULE AND NOT AN INLINE TERNARY. It was an inline ternary, on
 * the featured-model card, and the grid below it had none at all: every tile in
 * a gallery was a bare `<Image>`, so the gallery was the one surface in the
 * product where seeing an NFT did not let you open it. Fixing that by copying
 * the ternary into the tile would have made two copies of a rule that is
 * already subtle in two different ways:
 *
 *  1. THE TWO KINDS DO NOT SHARE A ROUTE SHAPE. An NFT is a path segment
 *     triple (`/assets/{network_name}/{contract}/{token_id}`); a collection is
 *     a contract with the network as a QUERY param
 *     (`/collection/{contract}?network_id=…`), because the single-collection
 *     route is V2 and a bare contract address is ambiguous across chains.
 *  2. EVERY PART IS OPTIONAL IN THE PAYLOAD. A gallery entry carries whatever
 *     the backend had; `network_id` in particular is absent on plenty of rows.
 *
 * NO LINK IS BETTER THAN A GUESSED ONE, and that is the rule this file
 * enforces: a missing segment returns `null` and the caller renders a plain
 * tile. The alternative — defaulting a network, or dropping the query param —
 * produces a link that 404s, which is worse than a tile that does not move,
 * and worse for a crawler too.
 *
 * Pure and injected with plain data so the combinations can be asserted
 * directly; a `page.tsx` ternary can only be checked by opening the page.
 */

/** What a link needs, in the shape the gallery payload already provides. */
export type GalleryLinkable = {
  /** `"Nft"` / `"Collection"` as the API sends it, in any casing. */
  modelType?: string | null;
  networkName?: string | null;
  contractAddress?: string | null;
  tokenId?: string | null;
  /** Numeric network id. Required for a collection; unused for an NFT. */
  networkId?: number | null;
  slug?: string | null;
};

function present(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The network id, only when it is genuinely usable in a URL.
 *
 * Matches `parseNetworkId`'s rule (a plain non-negative integer) rather than
 * inventing a looser one here — the two describe the same segment, read at
 * opposite ends of the same link, and a value this accepts but that one
 * rejects would produce a link whose destination refuses it.
 */
function usableNetworkId(value: number | null | undefined): string | null {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) return null;
  return String(value);
}

/**
 * The href for one gallery entry, or `null` when the payload cannot form one.
 *
 * Locale-prefixed, like every other in-app link: these are rendered by a Server
 * Component, so there is no router to add the segment afterwards.
 */
export function galleryItemHref(item: GalleryLinkable, lang: Locale): string | null {
  const type = present(item.modelType)?.toLowerCase() ?? "";

  if (type === "nft") {
    const network = present(item.networkName);
    const contract = present(item.contractAddress);
    // `"0"` is a legitimate token id and must survive: the guard is emptiness,
    // never falsiness.
    const tokenId = present(item.tokenId);
    if (!network || !contract || !tokenId) return null;
    return `/${lang}/assets/${encodeURIComponent(network)}/${encodeURIComponent(contract)}/${encodeURIComponent(tokenId)}`;
  }

  if (type === "collection") {
    const networkId = usableNetworkId(item.networkId);
    if (!networkId) return null;
    // Contract first, slug second — the same order, and the same fallback, the
    // featured-model card has always used.
    const key = present(item.contractAddress) ?? present(item.slug);
    if (!key) return null;
    return `/${lang}/collection/${encodeURIComponent(key)}?network_id=${networkId}`;
  }

  return null;
}

/** How the tile should name what it links to, for a screen reader. */
export function galleryItemKindLabel(
  modelType: string | null | undefined,
  isFa: boolean
): string {
  const type = present(modelType)?.toLowerCase() ?? "";
  if (type === "nft") return "NFT";
  if (type === "collection") return isFa ? "کالکشن" : "Collection";
  return isFa ? "آیتم" : "Item";
}
